import { useNavigate, useSearchParams } from "react-router-dom";
import { IconChevronRight, IconMessageCircle } from "@tabler/icons-react";
import { SearchBar } from "../../../../components/common/input";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination/pagination";
import { AppRoutes, createRoute } from "../../../../common/routes/app_routes";
import { useChatsPaginated } from "../../hooks/useChatsPaginated";
import type { Chat } from "../../data/types/chat";

const PAGE_SIZE = 10;

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return date.toLocaleDateString();
}

export function MyConversationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const setSearch = (value: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set("search", value);
        } else {
          params.delete("search");
        }
        params.set("page", "1");
        return params;
      },
      { replace: true },
    );
  };

  const setPage = (p: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", String(p));
        return params;
      },
      { replace: true },
    );
  };

  const { data: response, isLoading } = useChatsPaginated({
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
  });

  const chats = response?.data ?? [];
  const pageCount = response?.meta.pageCount ?? 1;
  const totalCount = response?.meta.totalCount ?? 0;

  const open = (chat: Chat) => {
    navigate(createRoute(AppRoutes.AI_SEARCH, { chatId: chat.id, results: "1" }));
  };

  const showEmpty = !isLoading && chats.length === 0;

  return (
    <div className="flex w-full flex-col items-center gap-6 px-20 py-10 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col gap-2">
          <Heading variant="h2" tone="dark">
            My Conversations
          </Heading>
          <Text variant="bodySm" tone="muted">
            Pick up where you left off — tap any chat to reopen it.
          </Text>
        </div>
      </div>

      <div className="flex w-full items-center gap-12 max-md:gap-3">
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search conversations"
          wrapperClassName="flex-1"
        />
      </div>

      <div className="flex w-full flex-col gap-3">
        {isLoading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <ShimmerLoader message="Fetching your conversations…" lines={4} className="max-w-lg" />
          </div>
        ) : showEmpty ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-10 text-center max-md:px-6 max-md:py-8">
            <IconMessageCircle size={40} className="text-textMuted" />
            <Heading variant="h3" tone="dark">
              {search.trim() ? "No conversations match your search" : "No conversations yet"}
            </Heading>
            <Text variant="bodySm" tone="muted">
              {search.trim()
                ? "Try a different search term."
                : "Ask Procurtal anything from the AI Search page and your chats will appear here."}
            </Text>
          </div>
        ) : (
          chats.map((chat) => (
            <button
              key={chat.id}
              type="button"
              onClick={() => open(chat)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-5 py-4 text-left transition-colors hover:bg-bgLight"
            >
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-accentCyanTint text-accentCyan700">
                <IconMessageCircle size={20} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center justify-between gap-3">
                  <Text variant="bodyBold" tone="dark" inline className="truncate">
                    {chat.title?.trim() || "Untitled conversation"}
                  </Text>
                  <Text variant="bodyXs" tone="muted" inline className="shrink-0">
                    {formatRelative(chat.lastMessageAt)}
                  </Text>
                </div>
                {chat.lastMessagePreview ? (
                  <Text variant="bodySm" tone="muted" className="truncate">
                    {chat.lastMessagePreview}
                  </Text>
                ) : null}
              </div>
              <IconChevronRight
                size={18}
                className="shrink-0 text-textMuted transition-transform group-hover:translate-x-0.5"
              />
            </button>
          ))
        )}
      </div>

      {!isLoading && pageCount > 1 && (
        <div className="flex w-full items-center justify-between">
          <Text variant="bodySm" tone="muted">
            {totalCount} conversation{totalCount !== 1 ? "s" : ""}
          </Text>
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

export default MyConversationsPage;
